/**
 * B6 · LEVELS — REST client for progression paths.
 *
 * Goes over the API and not PowerSync because scope isn't expressible as a
 * flat sync rule (drafts only for whoever administers) and because the
 * student's state is server-authoritative: if the client computed it, it
 * would say something different from the server the moment either
 * implementation changed. `docs/DATA_ACCESS_POLICY.md` §2.
 *
 * Same cookie-BFF transport as the rest of the repo's REST clients.
 */
import type { PrerequisiteEdge, TrackState } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/academy";

/**
 * VISUAL PATH flag, in a single place (no loose literals). The frontend's
 * other keys (`academy.progression`, `.prerequisites`, `.placement`,
 * `.upsell`) are decided by the SERVER and arrive resolved in the response:
 * they aren't duplicated here so the client can't hold a different opinion.
 */
export const PROGRESSION_PATH_FEATURE = "academy.progression.path";

/** Stage as returned by the path's design (not the student's state). */
export interface TrackStage {
  id: string;
  stageKey: string;
  position: number;
  title: string;
  anchorKind: string;
  anchorId?: string | null;
  unlockConfig: unknown;
  completionConfig: unknown;
  onComplete: unknown;
  authoredFields: unknown;
}

export interface Track {
  id: string;
  name: string;
  description?: string | null;
  programId?: string | null;
  scopeKey: string;
  config: Record<string, unknown>;
  status: string;
  stages: TrackStage[];
  edges: PrerequisiteEdge[];
}

export interface TracksResponse {
  tracks: Track[];
  /** The registry self-describes: the interface doesn't hand-carry the list. */
  scopes: string[];
  rewards: string[];
  enabled: boolean;
}

/** Next level offer. `quote` absent = card with no price block. */
export interface PendingOffer {
  offer_id: string;
  track_id: string;
  from_stage_id: string;
  next_stage_id?: string | null;
  next_stage_title?: string | null;
  valid_until?: string | null;
  quote?: {
    list_amount_usd_minor: number;
    offer_amount_usd_minor: number;
    savings_usd_minor: number;
    savings_pct: number;
    secondary_amount_minor?: number | null;
    exchange_rate?: number | null;
    installments_replaced: number;
    checkout_path: string;
  } | null;
}

const orgBase = (orgId: string): string => `${BASE}/${encodeURIComponent(orgId)}/progression`;

export function fetchTracks(orgId: string, drafts = false): Promise<TracksResponse> {
  // `drafts` is only honored by the server if you can also manage the
  // academy: requesting it from the client grants nothing.
  const search = new URLSearchParams({ drafts: String(drafts) });
  return apiJson<TracksResponse>(`${orgBase(orgId)}/tracks?${search.toString()}`);
}

/** State of the student's OWN progress on a track: what draws the path. */
export function fetchMyState(orgId: string, trackId: string): Promise<TrackState> {
  return apiJson<TrackState>(`${orgBase(orgId)}/tracks/${encodeURIComponent(trackId)}/my-state`);
}

export function fetchMyOffers(orgId: string): Promise<PendingOffer[]> {
  return apiJson<PendingOffer[]>(`${orgBase(orgId)}/offers`);
}

export function dismissOffer(orgId: string, offerId: string): Promise<{ ok: boolean }> {
  return apiJson<{ ok: boolean }>(
    `${orgBase(orgId)}/offers/${encodeURIComponent(offerId)}/dismiss`,
    { method: "POST" },
  );
}

/** Query keys (coherent invalidation across path, offer and admin). */
export const progressionQueryKeys = {
  tracks: (orgId: string | undefined, drafts: boolean) =>
    ["progression", "tracks", orgId ?? "none", drafts] as const,
  myState: (orgId: string | undefined, trackId: string | undefined) =>
    ["progression", "my-state", orgId ?? "none", trackId ?? "none"] as const,
  offers: (orgId: string | undefined) => ["progression", "offers", orgId ?? "none"] as const,
};
